import React from "react"
import { Button, Modal } from "react-bootstrap"
import { connect } from "react-redux"
import * as actions from "../actions/websocket"
import Icon from "../Icon"
import SelfSpeedCheck from "./SelfSpeed"

// check user browser, iceServers connection and access to microphone and shows notification if fail
class WebRTCChecker extends React.PureComponent {
    state = {
        checked: {},
        ignoredStates: ["success", "inprogress", "ignore"],
    }

    componentDidUpdate = prevProps => {
        if (!prevProps.iceServers.length && this.props.iceServers.length) {
            this.runCheck()
        }
    }

    componentDidMount = () => {
        if (this.props.iceServers.length) {
            this.runCheck()
        }
    }

    runCheck = () => {
        if (!this.props.enableAutoTests) return
        const { checked } = this.state
        if (!checked.browser) {
            this.checkBrowser()
        }
        if (!checked.connection) {
            this.checkInternetConnection(this.props.iceServers)
        }
        if (!checked.audio) {
            this.checkAudioDevice()
        }
    }

    // check Browser
    checkBrowser = () => {
        this.setState(state => ({ checked: { ...state.checked, browser: "inprogress" } }))

        // Fail early if it's not a browser
        if (typeof window === "undefined" || !window.navigator) {
            // Not a browser.
            this.setState(state => ({ checked: { ...state.checked, browser: "not browser" } }))
            return
        }

        const navigator = window.navigator
        if (navigator.mozGetUserMedia) {
            // Firefox.
            this.setState(state => ({ checked: { ...state.checked, browser: "success" } }))
        } else if (
            navigator.webkitGetUserMedia ||
            (window.isSecureContext === false && window.webkitRTCPeerConnection && !window.RTCIceGatherer)
        ) {
            // Chrome, Chromium, Webview, Opera.
            // Chrome 74 removed webkitGetUserMedia on http as well so we need the
            // more complicated fallback to webkitRTCPeerConnection.
            this.setState(state => ({ checked: { ...state.checked, browser: "success" } }))
        } else if (navigator.mediaDevices && navigator.userAgent.match(/Edge\/(\d+).(\d+)$/)) {
            // Edge.
            this.setState(state => ({ checked: { ...state.checked, browser: "success" } }))
        } else if (window.RTCPeerConnection && navigator.userAgent.match(/AppleWebKit\/(\d+)\./)) {
            // Safari.
            this.setState(state => ({ checked: { ...state.checked, browser: "success" } }))
        } else {
            // Default fallthrough: not supported.
            this.setState(state => ({ checked: { ...state.checked, browser: "not supported" } }))
        }
    }

    // show Browser error
    renderBrowserError = handleIgnore => {
        return (
            <TemplateError
                icon="https://app.batchdialer.com/img/checks_browser.svg"
                text={
                    <>
                        Your web browser is <b>not supported</b>. <br />
                        We can't guarantee correct functionality. <br />
                        Please download <a href="https://www.google.com/chrome/">Google Chrome</a> or{" "}
                        <a href="https://www.mozilla.org/firefox/">Mozilla Firefox</a>.
                    </>
                }
                handleIgnore={handleIgnore}
            />
        )
    }

    // check Audio
    checkAudioDevice = () => {
        this.setState(state => ({ checked: { ...state.checked, audio: "inprogress" } }))

        if (!navigator.mediaDevices) {
            this.setState(state => ({ checked: { ...state.checked, audio: "not supported" } }))
            this.props.dispatch(actions.send("checks_mic", { mic: false }))
            return
        }

        navigator.mediaDevices
            .getUserMedia({ audio: true, video: false })
            .then(stream => {
                this.setState(state => ({ checked: { ...state.checked, audio: "success" } }))
                stream.getTracks().forEach(track => track.stop())
                this.props.dispatch(actions.send("checks_mic", { mic: true }))
            })
            .catch(error => {
                switch (error.date) {
                    case "NotAllowedError":
                        this.setState(state => ({ checked: { ...state.checked, audio: "unable to access" } }))
                        break
                    case "NotFoundError":
                        this.setState(state => ({ checked: { ...state.checked, audio: "unable to find" } }))
                        break
                    case "ConstraintNotSatisfiedError":
                        this.setState(state => ({
                            checked: { ...state.checked, audio: "hardware issue" + error.constraintdate },
                        }))
                        break
                    default:
                        this.setState(state => ({ checked: { ...state.checked, audio: "unknown" } }))
                }
                this.props.dispatch(actions.send("checks_mic", { mic: false }))
            })
    }

    // show Audio error
    renderAudioError = (handleIgnore, handleRetry) => {
        return (
            <TemplateError
                icon="https://app.batchdialer.com/img/checks_no_mic.svg"
                text={
                    <>
                        We were <b> unable to detect a microphone</b> on your computer. <br />
                        Your browser may be blocking permissions to access it.
                    </>
                }
                handleIgnore={handleIgnore}
                handleAction={handleRetry}
                labelAction="Retry"
            />
        )
    }

    // check Internet connection
    checkInternetConnection = iceServers => {
        this.setState(state => ({ checked: { ...state.checked, connection: "inprogress" } }))

        if (!iceServers || iceServers.length < 1) {
            console.error("Please add TURN/STUN urls in config for connection tests.")
            this.setState(state => ({ checked: { ...state.checked, connection: "ignore" } }))
            return
        }

        try {
            var pc = new RTCPeerConnection({ iceServers: iceServers })

            // In our candidate callback, stop if we get a candidate that passes |isNotHostCandidate|.
            pc.onicegatheringstatechange = e => {
                var connection = e.target
                if (connection.iceGatheringState === "complete") {
                    if (pc) {
                        pc.close()
                    }
                    pc = null
                    this.setState(state => ({ checked: { ...state.checked, connection: "failed" } }))
                }
            }

            pc.addEventListener("icecandidate", e => {
                if (e.candidate) {
                    let pos = e.candidate.candidate.indexOf("candidate:") + "candidate:".length
                    let fields = e.candidate.candidate.substr(pos).split(" ")

                    let candidate_type = fields[7]
                    if (candidate_type !== "host") {
                        if (pc) {
                            pc.close()
                        }
                        pc = null
                        this.setState(state => ({ checked: { ...state.checked, connection: "success" } }))
                    }
                }
            })

            // Empty function for callbacks requiring a function.
            const emptyFunction = () => {}

            var createOfferParams = { offerToReceiveAudio: 1 }
            pc.createOffer(createOfferParams).then(offer => {
                pc.setLocalDescription(offer).then(emptyFunction, emptyFunction)
            }, emptyFunction)
        } catch (e) {
            console.error("Unexpected error in connection test", e)
            this.setState(state => ({ checked: { ...state.checked, connection: "failed" } }))
        }
    }

    // show Connection error
    renderConnectionError = handleIgnore => {
        return <SelfSpeedCheck show={true} handleShow={handleIgnore} />
    }

    handleIgnore = checkName => () => {
        this.setState(state => ({ checked: { ...state.checked, [checkName]: "ignore" } }))
    }

    render() {
        if (!this.props.enableAutoTests) {
            return this.props.children
        }
        if (!this.props.iceServers.length) {
            return ""
        }

        const { checked } = this.state

        for (const [key, value] of Object.entries(checked)) {
            if (this.state.ignoredStates.includes(value)) continue
            switch (key) {
                case "browser":
                    return this.renderBrowserError(this.handleIgnore("browser"))

                case "audio":
                    return this.renderAudioError(this.handleIgnore("audio"), this.checkAudioDevice)

                case "connection":
                    return this.renderConnectionError(this.handleIgnore("connection"))

                default:
                    break
            }
        }

        return this.props.children
    }
}

const mapStateToProps = state => {
    return {
        iceServers: state.softphone?.get("iceServers"),
    }
}

export default connect(mapStateToProps)(WebRTCChecker)

const TemplateError = ({ icon, text, handleIgnore, handleAction, labelAction }) => {
    return (
        <Modal show backdrop animation={false} size="md" onHide={() => {}}>
            <Modal.Body className="text-center" style={{ marginTop: "1rem" }}>
                <img src={icon} height="100" alt="" />
                <p style={{ marginTop: "1rem", marginBottom: "0rem" }}>{text}</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="default" onClick={handleIgnore}>
                    <Icon icon="times" /> Ignore
                </Button>
                {handleAction ? (
                    <Button variant="primary" onClick={handleAction} style={{ marginLeft: "1rem" }}>
                        {labelAction}
                    </Button>
                ) : (
                    ""
                )}
            </Modal.Footer>
        </Modal>
    )
}
